/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { useEffect, type MutableRefObject } from 'react';
import cx from 'classnames';
import type { HandleFilterType } from '../../../types/filterTypes';
import styles from './styles.module.scss';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { getCurrencySymbol } from '@/utils/priceHelpers';
import { priceFilterDisplayNames } from '../priceFilterDisplayNames';
import { formatPriceFilterLabel } from '@/utils/validator/priceCommaFormat';
import { ChipTag } from '../ChipTag';

type Props = {
  selectedFilter: HandleFilterType;
  index: number;
  handleFilter: (a: HandleFilterType) => void;
  selectedChipVariant?: 'checkmarkLeft' | 'checkmarkRight' | 'mobileDrawer';
  oneClickFilterRefs?: MutableRefObject<Array<MutableRefObject<HTMLLIElement>>>;
};

const labelMap = {
  lblRemove: 'label_cart_orderSummary_Remove',
};

const ProductFilters = ({
  selectedFilter,
  handleFilter,
  selectedChipVariant,
  oneClickFilterRefs,
  index,
}: Props): null | React.ReactElement => {
  const { lblRemove } = useLabels(labelMap);
  const locale = useLocale();
  const currencySymbol = getCurrencySymbol(locale);
  const priceFilterDimensions = Object.values(priceFilterDisplayNames);

  const { displayName, refinement = {}, dimensionName, quickFilter, checked } = selectedFilter;
  const isPriceFilter = priceFilterDimensions.includes(dimensionName?.toLowerCase());
  // @ts-expect-error fix type
  const { label } = refinement;
  const replaceRegex = new RegExp(`${currencySymbol}([\\d,]{4,}(?:\\.\\d+)?)`, 'g');
  const filterChipRef = oneClickFilterRefs?.current[index + 1];

  useEffect(() => {
    if (!filterChipRef) {
      return;
    }
    const objectObserver = new IntersectionObserver(
      ([entry]) => {
        entry.target.setAttribute(
          'data-testid',
          entry.isIntersecting ? `${label}-ocf-chip-visible` : `${label}-ocf-chip-hidden`,
        );
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      },
    );

    if (filterChipRef.current) {
      objectObserver.observe(filterChipRef.current);
    }

    return () => {
      objectObserver.disconnect();
    };
  }, [filterChipRef, label]);

  const labelForUI = (displayName: string) => {
    switch (displayName) {
      case 'Rating':
        return label.length < 2 ? `${label} stars` : label;
      default: {
        const formattedLabel = label.replace(replaceRegex, (match: string) => {
          const price = parseFloat(match.replace(currencySymbol, '').replace(',', ''));
          return currencySymbol + price.toLocaleString();
        });
        return isPriceFilter ? formatPriceFilterLabel(formattedLabel) : formattedLabel;
      }
    }
  };

  const ariaLabelForChip = (labelText: string) => {
    let labelName: string;
    switch (displayName) {
      case 'Rating': {
        const textWithStars = labelText.length < 2 ? `${labelText} stars` : labelText;
        labelName = textWithStars.includes('and')
          ? textWithStars.replace('and', 'stars and')
          : textWithStars;
        break;
      }
      case 'Price':
        labelName = labelText.includes('-') ? labelText.replace('-', 'to') : labelText;
        break;
      default:
        labelName = labelText;
    }

    return checked ? `${lblRemove} ${labelName} filter` : `select ${labelName} filter`;
  };
  return (
    <li
      key={`${label}${index}`}
      className={cx(styles.filtersChip, styles.inlineListItem)}
      ref={filterChipRef}
    >
      <ChipTag
        selectedChipVariant={selectedChipVariant}
        isChecked={checked}
        ariaLabel={ariaLabelForChip(label)}
        label={labelForUI(displayName)}
        handleFilter={() =>
          handleFilter({
            refinement: selectedFilter.refinement,
            checked: !checked,
            displayName,
            dimensionName,
            quickFilter,
            selectionType: 'One Click',
          })
        }
      />
    </li>
  );
};

export default ProductFilters;
